import React, {useState, useEffect, useRef} from 'react';
import {ContentDispatch} from 'framework';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {TabComponent} from 'componentLibrary/modules/TabComponent';
import {SubApplicationsTable} from 'go/components/modules/subApplications/SubApplicationsTable';
import REST from 'go/requests/rest';
import {
  STATUS_SAVED_FOR_LATER,
  STATUS_SUBMITTED_TO_FEMA,
  ENTITY_PAGE_TYPE_SUBAPPLICATION,
  ROLE_MAP,
  STATUS_SUBAWARD_ACCEPTED,
  STATUS_INELIGIBLE,
  STATUS_DISAPPROVED_BY_RECIPIENT,
  STATUS_NOT_SELECTED,
} from 'app-ui-componentlibrary/dist/common/constants';
import {getGrantActions, safeAdd, compileContent, isRecipientUser} from 'common/utils';
import {Pagination, Loader, Button, Input, Icon, Grid, Header} from 'semantic-ui-react';
import PermissionsUtility from 'go/security/permissionsUtility';
import {withRouter} from 'react-router-dom';
import queryString from 'query-string';
import {getTaskIdForEntity} from 'go/components/dynamic/modules/grantLandingPage/utils';
import {
  canStartSubapplication,
  extractOrgIdsFromPermissions,
} from 'go/security/permissions';
import {DeleteModal} from 'app-ui-componentlibrary';
import _ from 'lodash';
import moment from 'moment-timezone';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const SubApplicationsTableWrapper = (props) => {
  const {tabs, fundingOpportunityId, programId, organizationId, grantId} = props;
  const pageSize = _.get(props, 'pageSize', 10);
  const defaultSort = _.get(props, 'defaultSort', 'statusTimeStamp');
  const defaultSortDirection = _.get(props, 'defaultSortDirection', 'ASC');
  const {header, headerSize, headerAggregate} = props;

  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [requestQueue, setRequestQueue] = useState([]);
  const query = queryString.parse(_.get(props, 'location.search'));
  const [activeTabId, setActiveTabId] = useState(
    _.get(query, 'initialTab', _.get(tabs, '0.id'))
  );
  const [tabData, setTabData] = useState({});
  const tabsWithCount = _.map(tabs, (tab) => {
    const id = tab.id;
    return {
      ...tab,
      count: _.get(tabData, [id, 'count'], 0),
    };
  });
  const tab = _.find(tabsWithCount, (tab) => tab.id === activeTabId);
  const summary = _.get(tab, 'name', 'SubApplications') + ' table';
  const items = _.get(tabData, [activeTabId, 'items'], []);
  const tableProps = _.get(tab, 'tableProps', {});

  const [selectedColumn, setSelectedColumn] = useState(
    _.get(tableProps, 'defaultSort', defaultSort)
  );
  const [sortDirection, setSortDirection] = useState(
    _.get(tableProps, 'defaultSortDirection', defaultSortDirection)
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isClearingSearchTerm, setIsClearingSearchTerm] = useState(false);
  const [tableHeight, setTableHeight] = useState('63px');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [entityIdTbd, setEntityIdTbd] = useState('');
  const prevSearchTerm = usePrevious(searchTerm);
  const tableRef = useRef(null);
  const requestQueueRef = useRef();
  requestQueueRef.current = requestQueue;

  const activeTabFilteredCount = _.get(tabData, [activeTabId, 'filteredCount'], 0);
  const totalPages = Math.ceil(activeTabFilteredCount / pageSize);
  let showPrevButton = currentPage + 1 > 1 ? undefined : null;
  let showNextButton = currentPage + 1 < totalPages ? undefined : null;

  const setActiveTab = (tabId) => {
    setActiveTabId(tabId);
    const activeTab = _.find(tabs, (tab) => tab.id === tabId);
    const tabDefaultSort = _.get(activeTab, 'tableProps.defaultSort', defaultSort);
    const tabDefaultSortDirection = _.get(
      activeTab,
      'tableProps.defaultSortDirection',
      defaultSortDirection
    );
    setSelectedColumn(tabDefaultSort);
    setSortDirection(tabDefaultSortDirection);
    setCurrentPage(0);
    setSearchTerm('');
  };

  const setSelectedColumnProp = (columnName) => {
    setSelectedColumn(columnName);
  };

  const setSortDirectionProp = (direction) => {
    setSortDirection(direction);
    setCurrentPage(0);
  };

  const handlePageChange = (e, {activePage}) => {
    setCurrentPage(activePage - 1);
  };
  const handleStartSubApplication = () => {
    props.history.push('/subApplicationInitialize/fo/' + fundingOpportunityId);
  };

  const toggles = _.get(props, 'toggles');
  const fmagEnhancePt1 = _.includes(toggles, 'fmagEnhancePt1');
  const entityEdit = _.get(props, 'entityEdit', false);
  const canStartSubapp = canStartSubapplication(
    props.permissions,
    _.get(props, 'context.grant.organizationId'),
    _.get(props, 'context.grant.programId')
  );

  const handleTermChange = (e, entry) => {
    setSearchTerm(entry.value);
    setCurrentPage(0);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    getTabContents(activeTabId);
  };

  const onClearSearchTerm = () => {
    setIsClearingSearchTerm(true);
    setSearchTerm('');
  };

  useEffect(() => {
    getTabContents(activeTabId);
    if (_.get(tableProps, 'withPieChart', false)) {
      loadPieChartData();
    }
  }, []);

  useEffect(() => {
    if (!isInitialLoad) getTabContents(activeTabId);
  }, [selectedColumn, sortDirection, currentPage, activeTabId]);

  useEffect(() => {
    if (
      _.get(
        _.find(tabs, (tab) => tab.id === activeTabId),
        'tableProps.withPieChart',
        false
      )
    ) {
      loadPieChartData();
    }
  }, [activeTabId]);

  useEffect(() => {
    if (prevSearchTerm !== searchTerm && !isInitialLoad) {
      if (isClearingSearchTerm) {
        getTabContents(activeTabId);
        setIsClearingSearchTerm(false);
      } else {
        const timer = setTimeout(() => {
          getTabContents(activeTabId);
        }, 1200);

        return () => clearTimeout(timer);
      }
    }
  }, [prevSearchTerm, searchTerm, isClearingSearchTerm]);

  const getTabContents = (activeTab) => {
    setLoading(true);
    setRequestQueue([
      ...requestQueue,
      {
        type: 'getTabContentsAction',
        payload: {
          activeTab,
        },
      },
    ]);
  };

  const loadPieChartData = () => {
    const activeTab = _.find(tabs, (tab) => tab.id == activeTabId);
    const contents = _.get(activeTab, 'contents');
    if (!contents) return;
    const pieChart = _.find(contents, {id: 'subappsSubmittedToFEMAPieChart'});
    if (!pieChart) return;
    const dataPath = _.get(pieChart, 'dataPath');
    if (!dataPath) return;
    props.saveEntitySessionData(dataPath, {
      data: [],
      loading: true,
    });
    let userOrg;
    if (isRecipientUser(props.permissions))
      userOrg = extractOrgIdsFromPermissions(props.permissions)[0];
    if (activeTab.id === 'subAwardRecommendations') {
      REST.search
        .getSubawardrecommendationFedShareByFundingCategory(
          programId,
          fundingOpportunityId,
          organizationId,
          grantId
        )
        .then(REST.handler.handleResponse)
        .then((data) => {
          const items = _.map(data.items, (item) => item.inner_hits);
          props.saveEntitySessionData(dataPath, {
            data: items,
            loading: false,
          });
        });
    } else {
      if (organizationId == undefined && !isRecipientUser(props.permissions)) {
        REST.search
          .getSubapplicationFedShareByFundingCategoryForFo(
            programId,
            fundingOpportunityId,
            activeTab.statuses.join(',')
          )
          .then(REST.handler.handleResponse)
          .then((data) => {
            const items = _.map(data.items, (item) => item.inner_hits);
            props.saveEntitySessionData(dataPath, {
              data: items,
              loading: false,
            });
          });
      } else {
        REST.search
          .getSubapplicationFedShareByFundingCategory(
            programId,
            fundingOpportunityId,
            organizationId ? organizationId : userOrg,
            activeTab.statuses.join(',')
          )
          .then(REST.handler.handleResponse)
          .then((data) => {
            const items = _.map(data.items, (item) => item.inner_hits);
            props.saveEntitySessionData(dataPath, {
              data: items,
              loading: false,
            });
          });
      }
    }
  };

  const modifySubApp = (entityId, payload) => {
    setRequestQueue([
      ...requestQueue,
      {
        type: 'modifySubAppAction',
        payload: {
          entityId,
          payload,
        },
      },
    ]);
  };

  const updatePieChart = (oldFundingCategory, newFundingCategory, fedShare) => {
    const activeTab = _.find(tabs, (tab) => tab.id == activeTabId);
    const contents = _.get(activeTab, 'contents');
    if (!contents) return;
    const pieChart = _.find(contents, {id: 'subappsSubmittedToFEMAPieChart'});
    if (!pieChart) return;
    const dataPath = _.get(pieChart, 'dataPath');
    if (!dataPath) return;
    const amount = safeAdd(...fedShare);
    const currentState = _.cloneDeep(_.get(props, dataPath, {}));
    const addTo = _.find(currentState.data, {name: newFundingCategory});
    const subtractFrom = _.find(currentState.data, {name: oldFundingCategory});
    subtractFrom.value -= amount;
    if (!addTo) {
      const newEntry = {
        name: newFundingCategory,
        value: amount,
      };
      props.saveEntitySessionData(dataPath, {
        data: _.sortBy(
          [..._.filter(currentState.data, (fc) => fc.value !== 0), newEntry],
          (entry) => entry.name
        ),
        loading: false,
      });
    } else {
      addTo.value += amount;
      props.saveEntitySessionData(dataPath, {
        data: _.sortBy(
          _.filter(currentState.data, (fc) => fc.value !== 0),
          (entry) => entry.name
        ),
        loading: false,
      });
    }
  };

  const applicationPeriodNotOpen = () => {
    const {grantApplicationExtensions, fundingOpportunityData} = props;
    const aplcnEndDate = _.get(
      fundingOpportunityData,
      'aplcnGracePd',
      _.get(fundingOpportunityData, 'aplcnEndDate')
    );
    const appExtension = _.get(grantApplicationExtensions, '0');
    const actualEndDate = appExtension
      ? appExtension.extendedApplicationEndDate
      : aplcnEndDate;
    const appEndDate = moment(actualEndDate, 'YYYY-MM-DDTHH:mm:ssZ');
    const now = moment();
    return !now.isBefore(appEndDate);
  };

  useEffect(() => {
    if (requestQueue.length && !requestQueue[0].loading) {
      const action = {
        ...requestQueue[0],
      };
      action.loading = true;
      setRequestQueue([action, ...requestQueue.slice(1)]);
      const cb = () => {
        setRequestQueue(requestQueueRef.current.slice(1));
      };
      switch (action.type) {
        case 'getTabContentsAction':
          getTabContentsAction(action.payload.activeTab, cb, cb);
          break;
        case 'modifySubAppAction':
          modifySubAppAction(action.payload.entityId, action.payload.payload, cb, cb);
          break;
      }
    }
  }, [requestQueue]);

  const modifySubAppAction = (entityId, payload, cb, errorcb) => {
    REST.dynamicData
      .modifySubApp(null, payload, entityId)
      .then(REST.handler.handleResponse)
      .then(cb)
      .catch(errorcb);
  };

  const getTabContentsAction = (activeTab, cb, errorcb) => {
    setLoading(true);
    const selector =
      activeTab === 'subAwardRecommendations'
        ? 'get_paginated_sub_award_recommendations'
        : 'get_paginated_subapplications';
    const limit = pageSize;
    const order = sortDirection;
    const offset = currentPage * pageSize;
    const sort = selectedColumn;
    const term = searchTerm;
    let subappRequests = [];

    if (_.get(tableRef, 'current')) {
      let height = tableRef.current.getBoundingClientRect().height;
      setTableHeight(height);
    }
    let userOrg;
    let userOrgs;
    if (isRecipientUser(props.permissions)) {
      userOrgs = extractOrgIdsFromPermissions(props.permissions);
      userOrg = userOrgs[0];
    }
    // iterate over tabs to retrieve subapp data
    _.forEach(tabs, (tab) => {
      const {id, statuses, tableProps} = tab;
      const hideColumns = _.get(tableProps, 'hideColumns', []);
      const hideColumnsString = _.join(hideColumns, ',');
      if ((activeTab === id || isInitialLoad) && _.size(statuses) > 0) {
        const statusString = _.join(statuses, ',');
        if (activeTab === 'subAwardRecommendations') {
          subappRequests.push(
            getSubAwardRecData(
              id,
              fundingOpportunityId,
              programId,
              organizationId ? organizationId : userOrg,
              grantId,
              selector,
              limit,
              order,
              offset,
              sort,
              term,
              hideColumnsString
            )
          );
        } else {
          const tabSelector =
            id === 'subappsAwarded'
              ? userOrg
                ? 'get_paginated_subapps_by_subgrant_status_orgids_for_fo'
                : 'get_paginated_subapps_by_subgrant_status_for_fo'
              : selector;
          subappRequests.push(
            getSubapplicationData(
              id,
              fundingOpportunityId,
              programId,
              organizationId ? organizationId : userOrg,
              userOrgs ? userOrgs : null,
              tabSelector,
              limit,
              order,
              offset,
              statusString,
              sort,
              term,
              hideColumnsString
            )
          );
        }
      }
    });

    Promise.all(subappRequests)
      .then((subappResults) => {
        let newTabData = _.cloneDeep(tabData);
        _.forEach(subappResults, (subappData) => {
          let tabName = subappData.tabName;
          newTabData[tabName] = subappData;
        });
        setTabData(newTabData);
        setLoading(false);
        if (isInitialLoad) setIsInitialLoad(false);
        cb();
      })
      .catch((e) => {
        console.error('Error retrieving subapps');
        errorcb();
      });
  };

  const getSubAwardRecData = (
    tabName,
    fundingOpportunityId,
    programId,
    organizationId,
    grantId,
    selector,
    limit,
    order,
    offset,
    sort,
    term,
    hideColumns
  ) => {
    const {grantApplicationExtensions, fundingOpportunityData} = props;
    const aplcnEndDate = _.get(
      fundingOpportunityData,
      'aplcnGracePd',
      _.get(fundingOpportunityData, 'aplcnEndDate')
    );
    const appExtension = _.get(grantApplicationExtensions, '0');
    return REST.search
      .getPaginatedSubAwardRecs(
        fundingOpportunityId,
        programId,
        organizationId,
        grantId,
        selector,
        limit,
        order,
        offset,
        sort,
        term,
        hideColumns
      )
      .then(REST.handler.handleResponse)
      .then((data) => {
        const count = term
          ? _.get(tabData, [activeTabId, 'count'])
          : _.get(data, 'items[0].inner_hits.totalCount', 0);
        const filteredCount = _.get(data, 'items[0].inner_hits.filteredCount', 0);

        if (!_.includes(hideColumns, 'obligationAmount')) {
          const grantIds = _.uniq(
            _.map(_.get(data, 'items', []), (subApp) => {
              return _.get(subApp, 'grantId', '');
            })
          );

          return REST.obligation
            .callGetSubGrantTotalObligationAmountsForGrantIds({grantIds: grantIds})
            .then(REST.handler.handleResponse)
            .then((res) => {
              const transformedData = _.flatMap(res, (obj) =>
                _.keys(obj).map((id) => ({[id]: {...obj[id]}}))
              );

              const obligationData = _.reduce(
                transformedData,
                (acc, item) => {
                  return {...acc, ...item};
                },
                {}
              );
              const items = getItems(
                data,
                ENTITY_PAGE_TYPE_SUBAPPLICATION,
                organizationId,
                aplcnEndDate,
                appExtension,
                obligationData
              );

              const subAppData = {
                tabName,
                count,
                filteredCount,
                items,
              };

              return subAppData;
            })
            .catch((e) => {
              console.error('Error retrieving obligation data');
              return Promise.reject(e);
            });
        }

        const items = getItems(
          data,
          ENTITY_PAGE_TYPE_SUBAPPLICATION,
          organizationId,
          aplcnEndDate,
          appExtension
        );
        const subAwardRecData = {
          tabName,
          count,
          filteredCount,
          items,
        };
        return subAwardRecData;
      })
      .catch((e) => {
        console.error('Error retrieving subaward recs');
        return Promise.reject(e);
      });
  };

  const getSubapplicationData = (
    tabName,
    fundingOpportunityId,
    programId,
    organizationId,
    organizationIds,
    selector,
    limit,
    order,
    offset,
    statuses,
    sort,
    term,
    hideColumns
  ) => {
    const {grantApplicationExtensions, fundingOpportunityData} = props;
    const aplcnEndDate = _.get(
      fundingOpportunityData,
      'aplcnGracePd',
      _.get(fundingOpportunityData, 'aplcnEndDate')
    );
    const appExtension = _.get(grantApplicationExtensions, '0');
    if (organizationId == undefined) {
      return REST.search
        .getPaginatedSubAppsForFo(
          fundingOpportunityId,
          programId,
          limit,
          order,
          offset,
          statuses,
          sort,
          term,
          hideColumns,
          selector
        )
        .then(REST.handler.handleResponse)
        .then((data) => {
          const count = term
            ? _.get(tabData, [activeTabId, 'count'])
            : _.get(data, 'items[0].inner_hits.totalCount', 0);
          const filteredCount = _.get(data, 'items[0].inner_hits.filteredCount', 0);

          if (!_.includes(hideColumns, 'obligationAmount')) {
            const grantIds = _.uniq(
              _.map(_.get(data, 'items', []), (subApp) => {
                return _.get(subApp, 'grantId', '');
              })
            );

            return REST.obligation
              .callGetSubGrantTotalObligationAmountsForGrantIds({grantIds: grantIds})
              .then(REST.handler.handleResponse)
              .then((res) => {
                const transformedData = _.flatMap(res, (obj) =>
                  _.keys(obj).map((id) => ({[id]: {...obj[id]}}))
                );

                const obligationData = _.reduce(
                  transformedData,
                  (acc, item) => {
                    return {...acc, ...item};
                  },
                  {}
                );
                const items = getItems(
                  data,
                  'fundingOpportunity',
                  organizationId,
                  aplcnEndDate,
                  appExtension,
                  obligationData
                );

                const subAppData = {
                  tabName,
                  count,
                  filteredCount,
                  items,
                };

                return subAppData;
              })
              .catch((e) => {
                console.error('Error retrieving obligation data');
                return Promise.reject(e);
              });
          }

          const items = getItems(
            data,
            'fundingOpportunity',
            organizationId,
            aplcnEndDate,
            appExtension
          );
          const subAppData = {
            tabName,
            count,
            filteredCount,
            items,
          };

          return subAppData;
        })
        .catch((e) => {
          console.error('Error retrieving subapps');
          return Promise.reject(e);
        });
    }
    return REST.search
      .getPaginatedSubApps(
        fundingOpportunityId,
        programId,
        organizationId,
        organizationIds,
        selector,
        limit,
        order,
        offset,
        statuses,
        sort,
        term,
        hideColumns
      )
      .then(REST.handler.handleResponse)
      .then((data) => {
        const count = term
          ? _.get(tabData, [activeTabId, 'count'])
          : _.get(data, 'items[0].inner_hits.totalCount', 0);
        const filteredCount = _.get(data, 'items[0].inner_hits.filteredCount', 0);

        if (!_.includes(hideColumns, 'obligationAmount')) {
          const grantIds = _.uniq(
            _.map(_.get(data, 'items', []), (subApp) => {
              return _.get(subApp, 'grantId', '');
            })
          );

          return REST.obligation
            .callGetSubGrantTotalObligationAmountsForGrantIds({grantIds: grantIds})
            .then(REST.handler.handleResponse)
            .then((res) => {
              const transformedData = _.flatMap(res, (obj) =>
                _.keys(obj).map((id) => ({[id]: {...obj[id]}}))
              );

              const obligationData = _.reduce(
                transformedData,
                (acc, item) => {
                  return {...acc, ...item};
                },
                {}
              );
              const items = getItems(
                data,
                ENTITY_PAGE_TYPE_SUBAPPLICATION,
                organizationId,
                aplcnEndDate,
                appExtension,
                obligationData
              );

              const subAppData = {
                tabName,
                count,
                filteredCount,
                items,
              };
              return subAppData;
            })
            .catch((e) => {
              console.error('Error retrieving obligation data');
              return Promise.reject(e);
            });
        }

        const items = getItems(
          data,
          ENTITY_PAGE_TYPE_SUBAPPLICATION,
          organizationId,
          aplcnEndDate,
          appExtension
        );
        const subAppData = {
          tabName,
          count,
          filteredCount,
          items,
        };
        return subAppData;
      })
      .catch((e) => {
        console.error('Error retrieving subapps');
        return Promise.reject(e);
      });
  };

  const getItems = (
    data,
    entityPageType,
    organizationId,
    aplcnEndDate,
    appExtension,
    obligationData = {}
  ) => {
    const {permissions, token, toggles} = props;

    const items = _.map(_.get(data, 'items', []), (subApp) => {
      const subOrgId = _.get(subApp, 'subOrgId');
      const permissionUtil = new PermissionsUtility(token);
      const hasOrgSubOrgRelationshipForEntitySix = permissionUtil.isCurrentIdExistForEntitySix(
        organizationId,
        token
      );
      let parentList = permissionUtil.getParentOrgIdListForSubOrg(organizationId);
      let rolesToCheck = [ROLE_MAP.SUBAUTHREP.code, ROLE_MAP.SUBMEMBER.code];
      const entitiesEnabled = _.get(props, 'entitiesEnabled', []);
      const actions = getGrantActions(
        {...subApp, aplcnEndDate},
        toggles,
        permissions,
        entitiesEnabled,
        appExtension,
        subOrgId,
        hasOrgSubOrgRelationshipForEntitySix,
        rolesToCheck,
        entityPageType,
        parentList,
        null,
        null
      );

      if (obligationData != {}) {
        const subGrantId = _.get(subApp, 'subGrantId', '');
        const awardedObligation = _.get(
          obligationData,
          [subGrantId, 'totalAwardedAmount'],
          null
        );

        return {
          ...subApp,
          awardedObligation,
          actions,
        };
      }

      return {
        ...subApp,
        actions,
      };
    });

    return items;
  };

  const renderAdditionalContents = (activeTab, tabs) => {
    const tabData = _.find(tabs, (data) => data.id === activeTab);
    const contents = _.get(tabData, 'contents');

    if (!contents) {
      return null;
    } else {
      return contents.map((content, contentIndex) => {
        return content ? <ContentDispatch key={contentIndex} config={content} /> : null;
      });
    }
  };

  const doEntityAction = (action, entity) => {
    const {token, permissions, redirectNewTab} = props;
    const entityId = entity && entity.entityId;
    switch (action) {
      case 'viewSubApplication':
        const {status, subGrantId} = entity;
        if (
          (status === STATUS_SUBMITTED_TO_FEMA ||
            status === STATUS_SUBAWARD_ACCEPTED ||
            status === STATUS_INELIGIBLE ||
            status === STATUS_NOT_SELECTED) &&
          subGrantId
        ) {
          if (redirectNewTab)
            window.open(`/subGrant/${subGrantId}/subApplication`, '_blank');
          else props.history.push(`/subGrant/${subGrantId}/subApplication`);
        } else {
          if (redirectNewTab) window.open(`/subApplication/${entityId}/view`, '_blank');
          else props.history.push(`/subApplication/${entityId}/view`);
        }
        break;
      case 'continueSubApplication':
        if (redirectNewTab) window.open(`/subApplication/${entityId}/edit`, '_blank');
        else props.history.push(`/subApplication/${entityId}/edit`);
        break;
      case 'reviewSubApplication':
        getTaskIdForEntity(
          token,
          permissions,
          entity.entityId,
          'reviewSubapplication'
        ).then((taskId) => {
          props.history.push(`/tasks/${taskId}`);
        });
        break;
      case 'changeSubApplicationStatus':
        if (
          entity.status === STATUS_SAVED_FOR_LATER ||
          entity.status === STATUS_DISAPPROVED_BY_RECIPIENT
        ) {
          const topLevelParameters = {
            entityId: entity.entityId,
            entityType: entity.entityType,
            fundingOpportunityId: entity.fundingOpportunityId,
            programId: entity.programId,
            organizationId: entity.organizationId,
            subOrgId: entity.subOrgId,
            contextId: parseInt(entity.fundingOpportunityId),
            contextType: 'fo',
            applicationTitle: entity.payload.applicationTitle,
            grantId: props.allData.grantId,
          };
          const startingVariables = {
            ...topLevelParameters,
            submittedUserId: entity.payload.submitterLoginName,
            submittedTimestamp: entity.payload.submittedTimestamp,
          };
          REST.workflow
            .startsubApplicationProcess(
              token,
              'recipientReviewPassthrough',
              topLevelParameters,
              startingVariables
            )
            .then(REST.handler.handleResponse)
            .then((res) => {
              getTaskIdForEntity(
                token,
                permissions,
                entity.entityId,
                'reviewSubapplication',
                30
              ).then((taskId) => {
                props.history.push(`/tasks/${taskId}`);
              });
            })
            .catch((error) => {
              console.error('error');
            });
        }
        break;
      case 'deleteSubApplication':
        setEntityIdTbd(entity.entityId);
        setDeleteModalOpen(true);
        break;
      case 'submitToFEMA':
        getTaskIdForEntity(
          token,
          permissions,
          entity.entityId,
          'subAppSubmissionToFEMA'
        ).then((taskId) => {
          props.history.push(`/tasks/${taskId}`);
        });
        break;
      default:
        console.error('Unsupported action');
    }
  };

  const renderHeader = () => {
    let headerResult = header;
    if (headerAggregate) {
      let totalCount = 0;
      _.forEach(tabsWithCount, (tab) => {
        totalCount += tab.count;
      });
      headerResult = header + ' (' + totalCount + ')';
    }
    //id={header} needed for sideNav locators to work for onClick navigation
    return (
      <Header
        as={headerSize || 'h3'}
        id={header}
        content={compileContent(headerResult, props)}
      />
    );
  };

  //GMMGO-45447
  const cancelActionForModal = () => {
    setDeleteModalOpen(false);
    setEntityIdTbd('');
  };

  const deleteActionForModal = (token, entityId) => {
    REST.dynamicData
      .deleteEntity(token, entityId)
      .then(REST.handler.handleResponse)
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        console.log('There was an error in deletion');
      });
  };

  return (
    <>
      {fmagEnhancePt1 && canStartSubapp && entityEdit && (
        <React.Fragment>
          <Grid>
            <Grid.Row verticalAlign="middle">
              <Grid.Column width="4">{header ? renderHeader() : null}</Grid.Column>
              <Grid.Column className="startSubAppLink" width="5">
                <a id="startSubAppLink" onClick={() => handleStartSubApplication()}>
                  Start a subapplication
                </a>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </React.Fragment>
      )}
      {fmagEnhancePt1 && (!canStartSubapp || !entityEdit) && (
        <React.Fragment>
          <Grid>
            <Grid.Row verticalAlign="middle">
              <Grid.Column width="4">{header ? renderHeader() : null}</Grid.Column>
            </Grid.Row>
          </Grid>
        </React.Fragment>
      )}
      <TabComponent
        tabs={tabsWithCount}
        setActiveTab={setActiveTab}
        initialTab={activeTabId}
      />
      {renderAdditionalContents(activeTabId, tabsWithCount)}
      <div className="search-bar">
        <form id="search-form" onSubmit={onSearch}>
          <span>
            <Input>
              <Input
                aria-label={`Search by term`}
                title={`Search by term`}
                value={searchTerm}
                onChange={handleTermChange}
                onFocus={(e) => e.target.select()}
                style={{width: '380px'}}
              />
              <Button primary icon aria-label="Search Button" title="Search button">
                <Icon name="search" fitted />
              </Button>
            </Input>
            {searchTerm !== '' && (
              <a
                id="clear-search"
                href="#"
                className="padding-left"
                onClick={() => onClearSearchTerm()}
              >
                Clear search
              </a>
            )}
          </span>
        </form>
      </div>
      {loading ? (
        <div
          className="margin-top"
          style={{minHeight: tableHeight, display: 'flex', alignItems: 'center'}}
        >
          <Loader inline="centered" size="large" active>
            Loading...
          </Loader>
        </div>
      ) : (
        <>
          <div ref={tableRef}>
            <SubApplicationsTable
              subapplications={items}
              setSelectedColumnProp={setSelectedColumnProp}
              setSortDirectionProp={setSortDirectionProp}
              selectedColumn={selectedColumn}
              sortDirection={sortDirection}
              summary={summary}
              doEntityAction={doEntityAction}
              saveEntitySessionData={_.get(props, 'saveEntitySessionData')}
              modifySubApp={modifySubApp}
              updatePieChart={updatePieChart}
              applicationPeriodNotOpen={applicationPeriodNotOpen}
              {...tableProps}
            />
            {_.size(items) < 1 && (
              <div id="no-subapps-found" className="text-align-center">
                There are no subapplications available
              </div>
            )}

            {activeTabFilteredCount > pageSize && (
              <Pagination
                activePage={currentPage + 1}
                siblingRange={1}
                boundaryRange={1}
                prevItem={showPrevButton}
                nextItem={showNextButton}
                firstItem={showPrevButton}
                lastItem={showNextButton}
                ellipsisItem={undefined}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}

            <DeleteModal
              displayTitle={'Delete subapplication'}
              open={deleteModalOpen === true}
              cancelAction={() => cancelActionForModal()}
              deleteAction={() => deleteActionForModal(props.token, entityIdTbd)}
              displayText={
                'Are you sure you want to delete this subapplication? This action will remove the subapplication permanently from the FEMA GO system. This action cannot be undone'
              }
            />
          </div>
        </>
      )}
    </>
  );
};

const SubApplicationsTableWrapperWithRouter = withRouter(SubApplicationsTableWrapper);

export default connectDynamic(SubApplicationsTableWrapperWithRouter);
